import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { Copy, Download, Check } from "lucide-react";
import { stripMarkdown, downloadAssistantPdf } from "@/lib/aiExport";

/**
 * Action row that sits under every assistant message.
 *
 * Provides:
 *   - Copy (plain text, no markdown symbols)
 *   - Download PDF (Zynthoro-branded, blue accents, white background)
 */
export default function AssistantActions({ content, assistantName, testIdPrefix = "assist-msg" }) {
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const downloadPending = useRef(false);

  const copyTimer = useRef(null);
  const copyPending = useRef(false);
  useEffect(() => () => clearTimeout(copyTimer.current), []);

  const handleCopy = async () => {
    if (copyPending.current) return;
    copyPending.current = true;
    clearTimeout(copyTimer.current);
    setCopied(false);
    try {
      const clean = stripMarkdown(content || "");
      if (!clean.trim()) {
        toast.info("There is no text to copy yet.");
        return;
      }
      let success = false;
      try {
        await navigator.clipboard.writeText(clean);
        success = true;
      } catch {
        const previousFocus = document.activeElement;
        const selection = window.getSelection();
        const ranges = [];
        if (selection) {
          for (let i = 0; i < selection.rangeCount; i += 1) {
            ranges.push(selection.getRangeAt(i).cloneRange());
          }
        }
        const ta = document.createElement("textarea");
        ta.value = clean;
        ta.readOnly = true;
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        ta.style.top = "0";
        document.body.appendChild(ta);
        try {
          ta.focus();
          ta.select();
          success = document.execCommand("copy") === true;
        } finally {
          ta.remove();
          previousFocus?.focus?.({ preventScroll: true });
          if (selection) {
            selection.removeAllRanges();
            ranges.forEach((range) => selection.addRange(range));
          }
        }
      }
      if (!success) {
        toast.error("Copying was blocked by your browser. Select the message text and copy it manually.");
        return;
      }
      setCopied(true);
      copyTimer.current = setTimeout(() => setCopied(false), 1400);
    } catch {
      toast.error("The message could not be copied. Select the text and copy it manually.");
    } finally {
      copyPending.current = false;
    }
  };

  const handleDownload = async () => {
    if (downloadPending.current) return;
    if (!stripMarkdown(content || "").trim()) {
      toast.info("There is no text to download yet.");
      return;
    }
    downloadPending.current = true;
    setDownloading(true);
    try {
      await downloadAssistantPdf({ assistantName, content });
    } catch {
      toast.error("The PDF could not be prepared. Your message remains available; you can copy the text or try again.");
    } finally {
      downloadPending.current = false;
      setDownloading(false);
    }
  };

  return (
    <div className="flex items-center gap-1.5 mt-1.5">
      <button
        type="button"
        onClick={handleCopy}
        data-testid={`${testIdPrefix}-copy`}
        aria-label="Copy message"
        className="inline-flex items-center gap-1 text-[11px] font-medium text-[#555] hover:text-[#202B60] px-2 py-1 rounded-md border border-[#eee] hover:border-[#202B60] bg-white transition-colors"
      >
        {copied ? <Check size={12} /> : <Copy size={12} />}
        {copied ? "Copied" : "Copy"}
      </button>
      <button
        type="button"
        onClick={handleDownload}
        disabled={downloading}
        aria-busy={downloading}
        data-testid={`${testIdPrefix}-pdf`}
        aria-label="Download as PDF"
        className="inline-flex items-center gap-1 text-[11px] font-medium text-[#555] hover:text-[#202B60] px-2 py-1 rounded-md border border-[#eee] hover:border-[#202B60] bg-white transition-colors"
      >
        <Download size={12} />
        {downloading ? "Preparing PDF…" : "Download PDF"}
      </button>
    </div>
  );
}
